import React from "react";
import { Box, CircularProgress, Grid2, Typography } from "@mui/material";
import FormAutocomplete from "../../forms/FormAutocomplete";
import FormDatePicker from "../../forms/FormDatePicker";
import { Control, UseFormSetValue } from "react-hook-form";
import Tile from "../../../types/tile";
import Filter from "../../../types/filter";
import Option from "../../../types/option";
import FilterValue from "../../../types/filterValue";
import { useSkeletonLoader } from "../../../hooks/useSkeletonLoader";

interface Props {
  control: Control<Tile, any>;
  formValues: Tile;
  filters?: Filter[];
  filterLoading?: boolean;
  setValue: UseFormSetValue<Tile>;
  loadWithSkeleton: (
    element: React.ReactNode,
  ) =>
    | string
    | number
    | boolean
    | Iterable<React.ReactNode>
    | JSX.Element
    | null
    | undefined;
}

const FilterForm: React.FC<Props> = (props) => {
  const { control, formValues, filters, filterLoading, setValue } = props;

  const { loadWithSkeleton } = useSkeletonLoader(!filters || !filters.length);

  const getFilters = (type: Filter) => {
    let selectedFilter = formValues?.filters?.filter(
      (x) => x.filterId === type?.id,
    );

    if (type.id === 1 && !!selectedFilter?.length) {
      let filter = selectedFilter![0];
      return {
        id: +filter?.value,
        value: type?.filterItems?.find((x) => x.id === +filter?.value)?.value,
      } as Option;
    } else if (!!selectedFilter?.length) {
      let selectedValuesList = selectedFilter?.map((filter) => {
        const newOption = {
          id: +filter?.value,
          stringId: filter.value,
          value:
            type?.filterItems?.find((x) => x.id === +filter?.value)?.value ||
            type?.filterItems?.find((x) => x.stringId === filter?.value)
              ?.value ||
            "",
        } as Option;

        return newOption;
      });

      return selectedValuesList;
    }
  };

  const handleFilterSelect = (values?: FilterValue[], type?: Filter) => {
    const options: FilterValue[] = [];

    values &&
      values.forEach((value) => {
        if (!!value) {
          let option = {
            id: 0,
            filterId: type?.id,
            value:
              value.id && value.id > 0 ? value.id?.toString() : value.stringId,
          } as FilterValue;

          options!.push(option);
        }
      });

    let otherFilters =
      !!formValues.filters && formValues.filters.length > 0
        ? formValues.filters.filter((x) => !!x && x.filterId !== type?.id)
        : [];

    otherFilters.forEach((f: FilterValue) => {
      options.push(f);
    });

    setValue("filters", options ?? []);
  };

  return (
    <>
      <Box display="flex" flexDirection="row" width="100%" mt={2}>
        <Typography fontWeight="bold">Filters</Typography>
      </Box>
      {!filters?.length && filterLoading ? (
        <Box display="flex" justifyContent="center" width="100%">
          <CircularProgress sx={{ color: "primary.main" }} />
        </Box>
      ) : (
        <>
          {filters &&
            filters
              ?.filter((x) => x.id === 1)
              ?.map((filter) => (
                <Grid2 key={filter.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  {loadWithSkeleton(
                    <FormAutocomplete
                      control={control}
                      fieldName={`filterSelections[${filter?.id}].selectedValues`}
                      label={filter?.name}
                      multiple={false}
                      options={filter?.filterItems || []}
                      noOptionsText={
                        !!filter?.isDynamic && !formValues?.connections
                          ? "Select connection(s) to load filters"
                          : `No options available`
                      }
                      onChange={(e) => handleFilterSelect([e], filter)}
                      defaultValue={getFilters(filter) || undefined}
                      staticFilter
                    />,
                  )}
                </Grid2>
              ))}
          {formValues?.filters?.find(
            (f) =>
              !!f && f.filterId === 1 && (f.value === "8" || f.value === "9"),
          ) && (
            <>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {loadWithSkeleton(
                  <FormDatePicker
                    control={control}
                    fieldName={`filters.${formValues?.filters?.findIndex(
                      (f) =>
                        !!f &&
                        f.filterId === 1 &&
                        (f.value === "8" || f.value === "9"),
                    )}.afterDate`}
                    label="After"
                    required
                  />,
                )}
              </Grid2>
              {formValues?.filters?.find(
                (f) => !!f && f.filterId === 1 && f.value === "9",
              ) && (
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  {loadWithSkeleton(
                    <FormDatePicker
                      control={control}
                      fieldName={`filters.${formValues?.filters?.findIndex(
                        (f) => !!f && f.filterId === 1 && f.value === "9",
                      )}.beforeDate`}
                      label="Before"
                      required
                    />,
                  )}
                </Grid2>
              )}
            </>
          )}
          {filters &&
            filters
              ?.filter(
                (x) => x.id !== 1 && x.id !== 10 && x.filterItems?.length > 0,
              )
              ?.map((filter) => (
                <Grid2 key={filter.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  {loadWithSkeleton(
                    <FormAutocomplete
                      control={control}
                      fieldName={`filterSelections[${filter?.id}].selectedValues`}
                      label={filter?.name}
                      options={filter?.filterItems || []}
                      noOptionsText={
                        !!filter?.isDynamic && !formValues?.connections
                          ? "Select connection(s) to load filters"
                          : `No options available`
                      }
                      onChange={(e) => handleFilterSelect(e, filter)}
                      multiple
                      defaultValue={getFilters(filter) || []}
                      staticFilter
                    />,
                  )}
                </Grid2>
              ))}
        </>
      )}
    </>
  );
};

export default FilterForm;
